import { useQuery } from '@vue/apollo-composable';
import { computed, onUnmounted, toValue, watch, type MaybeRef } from 'vue';

import { GetMessengerDialogCountsDocument } from '@shared/api/graphql/gql/graphql';

import { useMessengerWebSocket } from './use-messenger-websocket';

// Коалесинг всплесков WS-событий: при серии dialog.updated (массовое взятие/закрытие)
// шлём один рефетч вместо десятка. 400 мс — незаметно для оператора, но гасит бурст.
const REFETCH_DEBOUNCE_MS = 400;

/**
 * Долгоживущий источник числа неотвеченных диалогов для сайдбара.
 *
 * Живёт на уровне layout-виджета всю сессию (в отличие от MessengerPage, который
 * монтируется только на своём роуте), поэтому бейдж в меню обновляется, даже когда
 * страница мессенджера закрыта. Apollo-кэш общий: когда страница открыта, оба
 * потребителя `messengerDialogCounts` делят одну запись кэша и не конфликтуют.
 *
 * Метрика бейджа — `unanswered` (диалоги `status = open AND waiting_since IS NOT NULL`).
 * Realtime — по приватному каналу `messenger.{organizationId}`, событие `dialog.updated`
 * (шлётся из DialogService при взятии/передаче/решении/переоткрытии/ответе оператора и
 * при входящем сообщении клиента, выставляющем waiting_since — проверено в backend).
 *
 * @param enabled Реактивный флаг доступности мессенджера. Пока false — ни запрос,
 *   ни WS-подписка не выполняются (нет прав → нет лишнего трафика).
 */
export const useMessengerUnansweredCount = (enabled: MaybeRef<boolean>) => {
  const enabledRef = computed<boolean>(() => toValue(enabled));

  const { result, refetch, onError } = useQuery(GetMessengerDialogCountsDocument, null, () => ({
    enabled: enabledRef.value,
    fetchPolicy: 'cache-and-network',
  }));

  onError((error) => {
    console.error('[useMessengerUnansweredCount] Ошибка запроса счётчика неотвеченных', error);
  });

  const unansweredCount = computed<number>(() => result.value?.messengerDialogCounts.unanswered ?? 0);

  const { subscribeToMessages, unsubscribe } = useMessengerWebSocket();

  // Debounced-рефетч счётчиков. refetch() возвращает undefined, пока запрос не запущен
  // (enabled=false) — гасим через ?. и .catch (ошибка уже логируется через onError).
  let refetchTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleRefetch = (): void => {
    if (refetchTimer !== null) {
      clearTimeout(refetchTimer);
    }
    refetchTimer = setTimeout(() => {
      refetchTimer = null;
      void refetch()?.catch(() => {
        // no-op: onError уже залогировал ошибку запроса.
      });
    }, REFETCH_DEBOUNCE_MS);
  };

  // Подписка гейтится на enabled: подписываемся только при наличии прав, отписываемся
  // при их потере. Смена организации уже покрыта watch внутри useMessengerWebSocket.
  let subscribed = false;
  const startRealtime = (): void => {
    if (subscribed) {
      return;
    }
    subscribed = true;
    subscribeToMessages(scheduleRefetch, 'dialog.updated');
  };
  const stopRealtime = (): void => {
    if (!subscribed) {
      return;
    }
    subscribed = false;
    unsubscribe();
  };

  watch(
    enabledRef,
    (value) => {
      if (value) {
        startRealtime();
      } else {
        stopRealtime();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (refetchTimer !== null) {
      clearTimeout(refetchTimer);
      refetchTimer = null;
    }
    stopRealtime();
  });

  return {
    unansweredCount,
  };
};
